// src/api/lenderDistribution.js
import { apiGet, extractMessage } from "../client";

/**
 * Output for Analytics.jsx pie:
 * [{ lender, share, approvalRate, topPerformer, avgLoan, satisfaction }]
 * - Null/empty lender names are shown as "Unassigned" so they appear in the chart.
 * - The `months` parameter controls period aggregation (1, 3, 6).
 */

const num = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  let s = String(v).trim();
  const isPct = s.endsWith("%");
  s = s.replace(/[%\s,]/g, "");
  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return isPct ? n : n;
};

const labelOrUnassigned = (v) => {
  const s = (v ?? "").toString().trim();
  return s ? s : "Unassigned";
};

/**
 * Fetch lender distribution aggregated over the past N months.
 * @param {{ months?: 1|3|6, productType?: string }} opts  productType "if" uses the Invoice Finance endpoint.
 * @returns {Promise<Array<{lender:string, share:number, approvalRate?:number, topPerformer?:number, avgLoan?:number, satisfaction?:number}>>}
 */
export async function getLenderDistribution(opts = {}) {
  const months = Number(opts.months ?? 1);
  const safeMonths = [1, 3, 6].includes(months) ? months : 1;
  const isIf = String(opts.productType || "").toLowerCase() === "if";

  const url = isIf
    ? `/api/method/dynesourcerapi.if_analytics.get_if_lender_distribution?months=${safeMonths}`
    : `/api/method/dynesourcerapi.api.get_lender_distribution?months=${safeMonths}`;
  const raw = await apiGet(url);
  const msg = extractMessage(raw);

  // API: [{ lender, approval_rate, top_performer_score, avg_loan_amount, borrower_satisfaction }]
  // or wrapped: { rows | lenders | data: [...] }
  const rowsRaw = Array.isArray(msg)
    ? msg
    : Array.isArray(msg?.rows)
      ? msg.rows
      : Array.isArray(msg?.lenders)
        ? msg.lenders
        : Array.isArray(msg?.data)
          ? msg.data
          : [];
  const rows = rowsRaw;

  let normalized = rows.map((r) => {
    const lender = labelOrUnassigned(
      r.lender ?? r.Lender ?? r.name ?? r.Name ?? r.title ?? r.label ?? r.key ?? null
    );

    // Pie weight: explicit share, else top_performer_score (primary for get_lender_distribution).
    // Do not use avg_loan_amount as slice weight — it skews the chart vs API intent.
    let share = num(r.share);
    if (!share) share = num(r.top_performer_score ?? r.topPerformer);
    if (!share) share = num(r.count ?? r.value ?? r.total ?? r.applications ?? r.volume);
    if (!share) share = 0;

    return {
      lender,
      share,
      approvalRate: num(r.approval_rate ?? r.approvalRate),
      topPerformer: num(r.top_performer_score ?? r.topPerformer),
      avgLoan: num(r.avg_loan_amount ?? r.avgLoan),
      satisfaction: num(r.borrower_satisfaction ?? r.satisfaction ?? r.nps),
    };
  });

  // If every share is 0, give each slice weight 1 so the pie renders
  const totalShare = normalized.reduce((s, r) => s + (r.share || 0), 0);
  if (normalized.length > 0 && totalShare === 0) {
    normalized = normalized.map((r) => ({ ...r, share: 1 }));
  }

  return normalized;
}
